import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCrmLeadCard,
  getCrmOrderPipelineCard,
  type CrmLeadCard,
  type CrmPipelineCard,
} from '../../../api';
import type { AdminManagerBrief } from '../../../api';
import type { CrmAccessParams } from '../../../crmAccess';
import { canCrmFullControl } from '../../../crmAccess';
import { CrmDealDrawer } from './CrmDealDrawer';
import type { CrmDealSnapshot, CrmOpenDeal } from './crmDealDeepLink';
import { formatAdminPanelError } from '../../../lib/adminApiError';
import { AdminOverlayPortal } from '../AdminOverlayPortal';

type Props = {
  deal: CrmOpenDeal;
  snapshot?: CrmDealSnapshot | null;
  access: CrmAccessParams;
  managers?: AdminManagerBrief[];
  focusTasksOnOpen?: boolean;
  onClose: () => void;
  onUpdated: () => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onNavigateMeasurement?: (id: number) => void;
  onNavigateClient360?: (userId: number) => void;
  onOpenMeasurementsForUser?: (userId: number) => void;
  onCancelOrder?: (
    orderId: number,
    opts?: { notifyClient: boolean; managerNote: string },
  ) => Promise<boolean>;
  canCancelOrder?: boolean;
  canEditOrderStatus?: boolean;
};

function seedOrderCard(deal: CrmOpenDeal, snapshot?: CrmDealSnapshot | null): CrmPipelineCard | null {
  if (deal.kind !== 'order' || !snapshot?.orderCard || snapshot.orderCard.id !== deal.id) return null;
  return snapshot.orderCard;
}

function seedLeadCard(deal: CrmOpenDeal, snapshot?: CrmDealSnapshot | null): CrmLeadCard | null {
  if (deal.kind !== 'lead' || !snapshot?.leadCard || snapshot.leadCard.id !== deal.id) return null;
  return snapshot.leadCard;
}

export function CrmUnifiedDealDrawer({
  deal,
  snapshot,
  access,
  managers,
  focusTasksOnOpen = false,
  onClose,
  onUpdated,
  hapticSelection,
  hapticSuccess,
  onNavigateMeasurement,
  onNavigateClient360,
  onOpenMeasurementsForUser,
  onCancelOrder,
  canCancelOrder = false,
  canEditOrderStatus = false,
}: Props) {
  const crmFull = canCrmFullControl(access);
  const initialOrder = seedOrderCard(deal, snapshot);
  const initialLead = seedLeadCard(deal, snapshot);
  const hasSeed = Boolean(initialOrder || initialLead);

  const [orderCard, setOrderCard] = useState<CrmPipelineCard | null>(initialOrder);
  const [leadCard, setLeadCard] = useState<CrmLeadCard | null>(initialLead);
  const [loading, setLoading] = useState(!hasSeed);
  const [warn, setWarn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadGen = useRef(0);

  const load = useCallback(
    async (opts?: { background?: boolean }) => {
      const background = opts?.background ?? hasSeed;
      const gen = ++loadGen.current;
      if (!background) {
        setLoading(true);
        setError(null);
        setWarn(null);
        setOrderCard(null);
        setLeadCard(null);
      }
      try {
        if (deal.kind === 'order') {
          const card = await getCrmOrderPipelineCard(deal.id);
          if (gen !== loadGen.current) return;
          setOrderCard(card);
          setError(null);
          setWarn(null);
        } else {
          const lead = await getCrmLeadCard(deal.id);
          if (gen !== loadGen.current) return;
          setLeadCard(lead);
          setError(null);
          setWarn(null);
        }
      } catch (err: unknown) {
        if (gen !== loadGen.current) return;
        const fallback = deal.kind === 'order' ? 'Не удалось открыть заказ' : 'Не удалось открыть лид';
        const seededOrder = initialOrder;
        const seededLead = initialLead;
        if (deal.kind === 'order' && seededOrder) {
          setOrderCard(seededOrder);
          setError(null);
          setWarn(formatAdminPanelError(err, `${fallback}. Показана копия из воронки.`));
          return;
        }
        if (deal.kind === 'lead' && seededLead) {
          setLeadCard(seededLead);
          setError(null);
          setWarn(formatAdminPanelError(err, `${fallback}. Показана копия из воронки.`));
          return;
        }
        setError(formatAdminPanelError(err, fallback));
      } finally {
        if (gen === loadGen.current) setLoading(false);
      }
    },
    [deal.kind, deal.id, hasSeed, initialOrder, initialLead],
  );

  useEffect(() => {
    const nextOrder = seedOrderCard(deal, snapshot);
    const nextLead = seedLeadCard(deal, snapshot);
    const seeded = Boolean(nextOrder || nextLead);
    setOrderCard(nextOrder);
    setLeadCard(nextLead);
    setError(null);
    setWarn(null);
    setLoading(!seeded);
    void load({ background: seeded });
  }, [deal.kind, deal.id, snapshot, load]);

  const dealKey = `${deal.kind}:${deal.id}`;

  if (loading && !orderCard && !leadCard) {
    return (
      <AdminOverlayPortal zIndex={80}>
        <div className="flex flex-1 items-center justify-center min-h-0">
          <p className="text-sm font-semibold" style={{ color: 'var(--text-2)' }}>
            Загрузка карточки…
          </p>
        </div>
      </AdminOverlayPortal>
    );
  }

  if (error && !orderCard && !leadCard) {
    return (
      <AdminOverlayPortal zIndex={80}>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 min-h-0">
          <p className="text-sm text-center leading-relaxed" style={{ color: '#FC814A' }}>
            {error}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="press px-4 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
            style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
          >
            Повторить
          </button>
          <button
            type="button"
            onClick={onClose}
            className="press px-4 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
            style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
          >
            Закрыть
          </button>
        </div>
      </AdminOverlayPortal>
    );
  }

  if (deal.kind === 'lead' && leadCard) {
    return (
      <>
        {warn && (
          <div
            className="fixed top-3 left-3 right-3 z-[90] rounded-xl px-3 py-2 text-[11px] leading-snug pointer-events-none"
            style={{
              background: 'rgba(252,129,74,0.14)',
              border: '1px solid rgba(252,129,74,0.35)',
              color: '#FC814A',
            }}
          >
            {warn}
          </div>
        )}
        <CrmDealDrawer
          key={dealKey}
          mode="lead"
          lead={leadCard}
          access={access}
          managers={managers}
          focusTasksOnOpen={focusTasksOnOpen}
          onNavigateMeasurement={onNavigateMeasurement}
          onNavigateClient360={onNavigateClient360}
          onClose={onClose}
          onUpdated={() => {
            onUpdated();
            void load({ background: true });
          }}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
        />
      </>
    );
  }

  if (deal.kind === 'order' && orderCard) {
    return (
      <>
        {warn && (
          <div
            className="fixed top-3 left-3 right-3 z-[90] rounded-xl px-3 py-2 text-[11px] leading-snug pointer-events-none"
            style={{
              background: 'rgba(252,129,74,0.14)',
              border: '1px solid rgba(252,129,74,0.35)',
              color: '#FC814A',
            }}
          >
            {warn}
          </div>
        )}
        <CrmDealDrawer
          key={dealKey}
          mode="order"
          card={orderCard}
          access={access}
          crmFullControl={crmFull}
          focusTasksOnOpen={focusTasksOnOpen}
          managers={managers}
          bitrixDealId={orderCard.bitrix_deal_id}
          bitrixSyncError={orderCard.bitrix_sync_error}
          canCancelOrder={canCancelOrder || canEditOrderStatus}
          onCancelOrder={onCancelOrder}
          onNavigateClient360={onNavigateClient360}
          onOpenMeasurementsForUser={onOpenMeasurementsForUser}
          onClose={onClose}
          onUpdated={() => {
            onUpdated();
            void load({ background: true });
          }}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
        />
      </>
    );
  }

  return (
    <AdminOverlayPortal zIndex={80}>
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 min-h-0">
        <p className="text-sm text-center" style={{ color: 'var(--text-3)' }}>
          Не удалось показать карточку сделки.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="press px-4 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-2)' }}
        >
          Закрыть
        </button>
      </div>
    </AdminOverlayPortal>
  );
}

/** @deprecated Import CrmUnifiedDealDrawer for new entry points; CrmDealDrawer remains the panel implementation. */
export { CrmDealDrawer } from './CrmDealDrawer';
